import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wrench,
  Droplets,
  Volume2,
  Thermometer,
  Wind,
  CalendarCheck,
  Fan,
  Sparkles,
  Sun,
  Snowflake,
  HelpCircle,
  Clock,
  CalendarClock,
  Camera,
  X,
  ArrowRight,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Phone as PhoneIcon,
  MessageSquare,
  Mail,
  CheckCircle2,
  Pencil,
} from "lucide-react";

import { useSeo } from "@/lib/useSeo";
import DemoNotice from "@/components/site/DemoNotice";
import RadioCardGroup from "@/components/site/RadioCardGroup";
import RequestConfirmation from "@/components/site/RequestConfirmation";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// === Option lists (must match backend Literal[] exactly) ===
const ISSUE_OPTIONS = [
  { value: "AC not cooling", label: "AC not cooling", icon: Snowflake },
  { value: "Unit leaking", label: "Unit leaking", icon: Droplets },
  { value: "Strange noise", label: "Strange noise", icon: Volume2 },
  { value: "Thermostat issue", label: "Thermostat issue", icon: Thermometer },
  { value: "Weak airflow", label: "Weak airflow", icon: Wind },
  { value: "Maintenance request", label: "Maintenance request", icon: CalendarCheck },
  { value: "Replacement quote", label: "Replacement quote", icon: Fan },
  { value: "Indoor air quality question", label: "Indoor air quality question", icon: Sparkles },
];

const URGENCY_OPTIONS = [
  {
    value: "Today if available",
    label: "Today if available",
    description: "We'll flag this as a high-priority callback.",
    icon: Sun,
  },
  {
    value: "This week",
    label: "This week",
    description: "Routine but not urgent — quick callback.",
    icon: Clock,
  },
  {
    value: "Maintenance / non-urgent",
    label: "Maintenance / non-urgent",
    description: "Tune-up or routine check on your schedule.",
    icon: CalendarCheck,
  },
  {
    value: "Replacement estimate",
    label: "Replacement estimate",
    description: "You're considering a new system.",
    icon: Fan,
  },
];

const COOLING_OPTIONS = [
  { value: "Yes", label: "Yes — it's cooling" },
  { value: "No", label: "No — warm air only" },
  { value: "Somewhat", label: "Somewhat — weak" },
  { value: "Not sure", label: "Not sure" },
];

const SYSTEM_AGE_OPTIONS = [
  "Under 5 years",
  "5\u201310 years",
  "10\u201315 years",
  "15+ years",
  "Not sure",
];

const PREFERRED_CONTACT_OPTIONS = [
  { value: "Call", label: "Call", icon: PhoneIcon },
  { value: "Text", label: "Text", icon: MessageSquare },
  { value: "Email", label: "Email", icon: Mail },
];

const AREA_PILLS = [
  { slug: "cape-coral", name: "Cape Coral" },
  { slug: "fort-myers", name: "Fort Myers" },
  { slug: "naples", name: "Naples" },
  { slug: "bonita-springs", name: "Bonita Springs" },
  { slug: "estero", name: "Estero" },
  { slug: "lee-county", name: "Lee County" },
  { slug: "collier-county", name: "Collier County" },
];

const AREA_SLUG_TO_NAME = AREA_PILLS.reduce((acc, a) => {
  acc[a.slug] = a.name;
  return acc;
}, {});

const STEP_LABELS = ["The issue", "Your home", "Contact details", "Review"];

const INITIAL_FORM = {
  full_name: "",
  phone: "",
  email: "",
  city: "",
  issue_type: "",
  urgency: "",
  system_age: "",
  is_cooling_now: "",
  preferred_contact: "",
  notes: "",
  photo_filename: null,
  photo_size_bytes: null,
  intent_param: null,
  area_param: null,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidPhone(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
}

function computeRecommendedNextStepClient(issue_type, urgency) {
  if (issue_type === "AC not cooling" && urgency === "Today if available")
    return "High priority callback";
  if (issue_type === "Replacement quote" || urgency === "Replacement estimate")
    return "Send replacement estimate intake";
  if (issue_type === "Maintenance request" || urgency === "Maintenance / non-urgent")
    return "Schedule tune-up";
  if (issue_type === "Thermostat issue") return "Troubleshooting callback";
  if (issue_type === "Indoor air quality question")
    return "IAQ consultation follow-up";
  if (["Unit leaking", "Strange noise", "Weak airflow"].includes(issue_type))
    return "Diagnostic callback";
  return "Callback to qualify";
}

function buildOfflineSubmission(formData) {
  return {
    ...formData,
    id: `offline-${Date.now()}`,
    is_demo: true,
    created_at: new Date().toISOString(),
    recommended_next_step: computeRecommendedNextStepClient(
      formData.issue_type,
      formData.urgency
    ),
    pipeline_status: "New Lead \u2192 Qualifying",
    _offline: true,
  };
}

function prefillFromParams(intentParam, areaParam) {
  const next = { ...INITIAL_FORM };
  if (intentParam === "maintenance") {
    next.issue_type = "Maintenance request";
    next.urgency = "Maintenance / non-urgent";
  } else if (intentParam === "replacement") {
    next.issue_type = "Replacement quote";
    next.urgency = "Replacement estimate";
  } else if (intentParam === "ac-repair") {
    next.issue_type = "AC not cooling";
  } else if (intentParam === "indoor-air-quality") {
    next.issue_type = "Indoor air quality question";
  } else if (intentParam === "thermostat") {
    next.issue_type = "Thermostat issue";
  }
  if (areaParam && AREA_SLUG_TO_NAME[areaParam]) {
    next.city = AREA_SLUG_TO_NAME[areaParam];
  }
  next.intent_param = intentParam || null;
  next.area_param = areaParam || null;
  return next;
}

function formatBytes(n) {
  if (n == null) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// ============================================================
// Form layout helpers
// ============================================================

function FieldLabel({ htmlFor, required, children, hint }) {
  return (
    <div className="mb-1.5 flex items-baseline justify-between gap-3">
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-navy">
        {children}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {hint && (
        <span className="text-[11px] font-medium text-navy/50">{hint}</span>
      )}
    </div>
  );
}

function FieldError({ id, message, testId }) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      data-testid={testId}
      className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-600"
    >
      <AlertCircle className="h-3 w-3" />
      {message}
    </p>
  );
}

function inputClass(hasError) {
  return `w-full rounded-xl border bg-white px-3.5 py-3 text-sm text-navy placeholder:text-navy/40 transition-colors focus:outline-none focus:ring-2 focus:ring-aqua/60 ${
    hasError ? "border-red-400" : "border-mist-line"
  }`;
}

function SectionHeading({ step, children }) {
  return (
    <div className="mb-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-aqua-deep">
        Step {step + 1} of 4 · {STEP_LABELS[step]}
      </p>
      <h2 className="mt-1.5 font-display text-2xl font-bold leading-tight tracking-tight text-navy sm:text-3xl">
        {children}
      </h2>
    </div>
  );
}

// ============================================================
// Steps
// ============================================================

function StepIssue({ data, errors, setField }) {
  return (
    <div className="space-y-7" data-testid="step-issue">
      <SectionHeading step={0}>What&rsquo;s going on?</SectionHeading>
      <div>
        <p className="mb-3 text-[13px] font-semibold text-navy">
          Issue type<span className="ml-1 text-red-500">*</span>
        </p>
        <RadioCardGroup
          name="issue_type"
          testIdPrefix="issue"
          value={data.issue_type}
          onChange={(v) => setField("issue_type", v)}
          options={ISSUE_OPTIONS}
          columns={2}
          error={errors.issue_type}
          ariaLabel="Issue type"
        />
      </div>

      <div>
        <p className="mb-3 text-[13px] font-semibold text-navy">
          Urgency<span className="ml-1 text-red-500">*</span>
        </p>
        <RadioCardGroup
          name="urgency"
          testIdPrefix="urgency"
          value={data.urgency}
          onChange={(v) => setField("urgency", v)}
          options={URGENCY_OPTIONS}
          columns={2}
          error={errors.urgency}
          ariaLabel="Urgency"
        />
      </div>

      <div>
        <p className="mb-3 text-[13px] font-semibold text-navy">
          Is the AC cooling at all right now?
          <span className="ml-1 text-red-500">*</span>
        </p>
        <RadioCardGroup
          name="is_cooling_now"
          testIdPrefix="cooling"
          value={data.is_cooling_now}
          onChange={(v) => setField("is_cooling_now", v)}
          options={COOLING_OPTIONS}
          columns={4}
          error={errors.is_cooling_now}
          ariaLabel="Cooling status right now"
        />
      </div>
    </div>
  );
}

function StepHome({ data, errors, setField, fileRef, onFileChange, removePhoto }) {
  return (
    <div className="space-y-7" data-testid="step-home">
      <SectionHeading step={1}>Your home</SectionHeading>

      <div>
        <FieldLabel htmlFor="sr-city" required>
          City in Southwest Florida
        </FieldLabel>
        <input
          id="sr-city"
          name="city"
          type="text"
          autoComplete="address-level2"
          value={data.city}
          onChange={(e) => setField("city", e.target.value)}
          aria-invalid={!!errors.city}
          aria-describedby={errors.city ? "sr-city-err" : undefined}
          placeholder="e.g., Cape Coral"
          className={inputClass(!!errors.city)}
          data-testid="city-input"
        />
        <FieldError id="sr-city-err" message={errors.city} testId="city-error" />
        <ul className="mt-3 flex flex-wrap gap-2" aria-label="Quick city picker">
          {AREA_PILLS.map((a) => {
            const active = data.city.trim().toLowerCase() === a.name.toLowerCase();
            return (
              <li key={a.slug}>
                <button
                  type="button"
                  data-testid={`city-pill-${a.slug}`}
                  onClick={() => setField("city", a.name)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? "border-aqua-deep bg-aqua/10 text-navy"
                      : "border-mist-line bg-white text-navy/70 hover:border-aqua/45"
                  }`}
                >
                  <span className="h-1 w-1 rounded-full bg-aqua" />
                  {a.name}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div>
        <FieldLabel htmlFor="sr-system-age" required>
          System age
        </FieldLabel>
        <select
          id="sr-system-age"
          name="system_age"
          value={data.system_age}
          onChange={(e) => setField("system_age", e.target.value)}
          aria-invalid={!!errors.system_age}
          aria-describedby={errors.system_age ? "sr-system-age-err" : undefined}
          className={inputClass(!!errors.system_age)}
          data-testid="system-age-select"
        >
          <option value="">Select one…</option>
          {SYSTEM_AGE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <FieldError id="sr-system-age-err" message={errors.system_age} testId="system-age-error" />
      </div>

      <div>
        <FieldLabel htmlFor="sr-notes" hint="optional">
          Notes for the technician
        </FieldLabel>
        <textarea
          id="sr-notes"
          name="notes"
          rows={4}
          value={data.notes}
          onChange={(e) => setField("notes", e.target.value)}
          placeholder="When it started, recent service, weird smells, breaker tripping, ice on the lines, etc."
          className={inputClass(false)}
          data-testid="notes-input"
        />
      </div>

      <div>
        <FieldLabel hint="optional">Photo of the unit</FieldLabel>
        {data.photo_filename ? (
          <div
            data-testid="photo-chip"
            className="flex items-center justify-between gap-3 rounded-xl border border-aqua/30 bg-aqua/[0.05] px-3.5 py-2.5"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-navy text-aqua">
                <Camera className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-navy" title={data.photo_filename}>
                  {data.photo_filename}
                </p>
                <p className="text-[11px] text-navy/55">
                  {formatBytes(data.photo_size_bytes)} · filename + size only
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={removePhoto}
              data-testid="photo-remove"
              className="inline-flex items-center gap-1 rounded-full border border-mist-line bg-white px-3 py-1 text-xs font-semibold text-navy/75 hover:text-navy"
            >
              <X className="h-3 w-3" />
              Remove
            </button>
          </div>
        ) : (
          <label
            htmlFor="sr-photo"
            data-testid="photo-trigger"
            className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-mist-line bg-white px-4 py-5 text-sm font-medium text-navy/75 transition-colors hover:border-aqua/45 hover:text-navy"
          >
            <Camera className="h-4 w-4 text-aqua-deep" />
            Choose a photo (optional)
          </label>
        )}
        <input
          ref={fileRef}
          id="sr-photo"
          type="file"
          accept="image/*"
          onChange={onFileChange}
          className="sr-only"
          data-testid="photo-input"
        />
        <p className="mt-2 text-[11px] text-navy/55">
          Photo is captured as filename only in this demo &mdash; no image is
          uploaded.
        </p>
      </div>
    </div>
  );
}

function StepContact({ data, errors, setField }) {
  return (
    <div className="space-y-7" data-testid="step-contact">
      <SectionHeading step={2}>Contact details</SectionHeading>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <FieldLabel htmlFor="sr-name" required>
            Full name
          </FieldLabel>
          <input
            id="sr-name"
            name="full_name"
            type="text"
            autoComplete="name"
            value={data.full_name}
            onChange={(e) => setField("full_name", e.target.value)}
            aria-invalid={!!errors.full_name}
            aria-describedby={errors.full_name ? "sr-name-err" : undefined}
            placeholder="Jane Homeowner"
            className={inputClass(!!errors.full_name)}
            data-testid="name-input"
          />
          <FieldError id="sr-name-err" message={errors.full_name} testId="full_name-error" />
        </div>
        <div>
          <FieldLabel htmlFor="sr-phone" required>
            Phone
          </FieldLabel>
          <input
            id="sr-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            value={data.phone}
            onChange={(e) => setField("phone", e.target.value)}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? "sr-phone-err" : undefined}
            placeholder="(555) 000-0142"
            className={inputClass(!!errors.phone)}
            data-testid="phone-input"
          />
          <FieldError id="sr-phone-err" message={errors.phone} testId="phone-error" />
        </div>
        <div className="md:col-span-2">
          <FieldLabel htmlFor="sr-email" required>
            Email
          </FieldLabel>
          <input
            id="sr-email"
            name="email"
            type="email"
            autoComplete="email"
            value={data.email}
            onChange={(e) => setField("email", e.target.value)}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "sr-email-err" : undefined}
            placeholder="you@example.com"
            className={inputClass(!!errors.email)}
            data-testid="email-input"
          />
          <FieldError id="sr-email-err" message={errors.email} testId="email-error" />
        </div>
      </div>

      <div>
        <p className="mb-3 text-[13px] font-semibold text-navy">
          Preferred contact method
          <span className="ml-1 text-red-500">*</span>
        </p>
        <RadioCardGroup
          name="preferred_contact"
          testIdPrefix="contact"
          value={data.preferred_contact}
          onChange={(v) => setField("preferred_contact", v)}
          options={PREFERRED_CONTACT_OPTIONS}
          columns={3}
          error={errors.preferred_contact}
          ariaLabel="Preferred contact method"
        />
      </div>

      <p className="rounded-xl border border-mist-line bg-mist px-4 py-3 text-xs leading-relaxed text-navy/65">
        Demo intake &mdash; no real emergency dispatch. Your submission will be
        saved as a sample lead for PrismForge Digital review.
      </p>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-mist-line py-2.5 last:border-b-0">
      <span className="text-[12px] font-medium uppercase tracking-[0.1em] text-navy/55">
        {label}
      </span>
      <span className="max-w-[65%] break-words text-right text-[14px] font-semibold text-navy">
        {value || "\u2014"}
      </span>
    </div>
  );
}

function ReviewGroup({ title, onEdit, testId, children }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-mist-line bg-white p-5 shadow-soft sm:p-6"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-bold tracking-tight text-navy">
          {title}
        </h3>
        <button
          type="button"
          onClick={onEdit}
          data-testid={`${testId}-edit`}
          className="inline-flex items-center gap-1 rounded-full border border-mist-line bg-white px-2.5 py-1 text-xs font-semibold text-navy/75 transition-colors hover:border-aqua/45 hover:text-navy"
        >
          <Pencil className="h-3 w-3 text-aqua-deep" />
          Edit
        </button>
      </div>
      <div className="divide-y divide-mist-line">{children}</div>
    </section>
  );
}

function StepReview({ data, goTo }) {
  const photoText = data.photo_filename
    ? `${data.photo_filename}${
        data.photo_size_bytes ? ` (${formatBytes(data.photo_size_bytes)})` : ""
      }`
    : "—";
  return (
    <div className="space-y-5" data-testid="step-review">
      <SectionHeading step={3}>Review &amp; submit</SectionHeading>

      <ReviewGroup
        title="The issue"
        onEdit={() => goTo(0)}
        testId="review-issue"
      >
        <ReviewRow label="Issue" value={data.issue_type} />
        <ReviewRow label="Urgency" value={data.urgency} />
        <ReviewRow label="Cooling now" value={data.is_cooling_now} />
      </ReviewGroup>

      <ReviewGroup
        title="Your home"
        onEdit={() => goTo(1)}
        testId="review-home"
      >
        <ReviewRow label="City" value={data.city} />
        <ReviewRow label="System age" value={data.system_age} />
        <ReviewRow label="Notes" value={data.notes?.trim()} />
        <ReviewRow label="Photo" value={photoText} />
      </ReviewGroup>

      <ReviewGroup
        title="Contact details"
        onEdit={() => goTo(2)}
        testId="review-contact"
      >
        <ReviewRow label="Name" value={data.full_name} />
        <ReviewRow label="Phone" value={data.phone} />
        <ReviewRow label="Email" value={data.email} />
        <ReviewRow label="Preferred contact" value={data.preferred_contact} />
      </ReviewGroup>
    </div>
  );
}

// ============================================================
// Main page
// ============================================================

export default function ServiceRequest() {
  useSeo({
    title:
      "Request HVAC Service — Demo intake | Sun Chaser Cooling — PrismForge Digital Demo",
    description:
      "Multi-step demo HVAC service request intake. Sun Chaser Cooling is a sample HVAC site by PrismForge Digital — no real emergency dispatch.",
  });

  const [searchParams] = useSearchParams();
  const intentParam = searchParams.get("intent");
  const areaParam = searchParams.get("area");

  const [formData, setFormData] = useState(() =>
    prefillFromParams(intentParam, areaParam)
  );
  const [step, setStep] = useState(0);
  const [maxStepSeen, setMaxStepSeen] = useState(0);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [serverError, setServerError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [submission, setSubmission] = useState(null);
  const fileRef = useRef(null);
  const formCardRef = useRef(null);

  const setField = (key, value) => {
    setFormData((p) => ({ ...p, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validateStep = (idx) => {
    const e = {};
    if (idx === 0) {
      if (!formData.issue_type) e.issue_type = "Pick the closest match.";
      if (!formData.urgency) e.urgency = "Pick how urgent this is.";
      if (!formData.is_cooling_now)
        e.is_cooling_now = "Tell us if the AC is cooling.";
    } else if (idx === 1) {
      if (!formData.city.trim()) e.city = "Where are you located?";
      if (!formData.system_age) e.system_age = "Pick a system age range.";
    } else if (idx === 2) {
      if (!formData.full_name.trim()) e.full_name = "Please enter your name.";
      if (!formData.phone.trim()) e.phone = "Please enter your phone number.";
      else if (!isValidPhone(formData.phone))
        e.phone = "Use a 10-digit US phone number.";
      if (!formData.email.trim()) e.email = "Please enter your email.";
      else if (!EMAIL_RE.test(formData.email))
        e.email = "Please enter a valid email address.";
      if (!formData.preferred_contact)
        e.preferred_contact = "Pick a preferred contact method.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const scrollToFormTop = () => {
    formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const next = () => {
    if (validateStep(step)) {
      const target = Math.min(3, step + 1);
      setStep(target);
      setMaxStepSeen((m) => Math.max(m, target));
      scrollToFormTop();
    }
  };

  const back = () => {
    if (step > 0) {
      setStep(step - 1);
      scrollToFormTop();
    }
  };

  const goTo = (idx) => {
    // allow jumping back, or forward to any step you've already validated through
    if (idx <= step || idx <= maxStepSeen) {
      setStep(idx);
      scrollToFormTop();
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setField("photo_filename", f.name);
    setField("photo_size_bytes", f.size);
  };

  const removePhoto = () => {
    setField("photo_filename", null);
    setField("photo_size_bytes", null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const validateAll = () =>
    validateStep(0) && validateStep(1) && validateStep(2);

  const submit = async ({ offline = false } = {}) => {
    if (!validateAll()) {
      // Jump to the first step with an error
      if (!formData.issue_type || !formData.urgency || !formData.is_cooling_now)
        setStep(0);
      else if (!formData.city.trim() || !formData.system_age) setStep(1);
      else setStep(2);
      scrollToFormTop();
      return;
    }

    setStatus("submitting");
    setServerError(null);

    if (offline) {
      setSubmission(buildOfflineSubmission(formData));
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/service-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
        } catch (_) {
          /* ignore */
        }
        throw new Error(detail);
      }
      const data = await res.json();
      setSubmission(data);
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
      setServerError(err?.message || "Network error");
      setAttempts((a) => a + 1);
    }
  };

  const retry = () => {
    setStatus("idle");
    setServerError(null);
    submit();
  };

  // Step pills
  const renderPills = () => (
    <ol
      data-testid="service-request-step-pills"
      className="flex flex-wrap items-center gap-x-2 gap-y-3"
    >
      {STEP_LABELS.map((label, i) => {
        const isActive = step === i;
        const isComplete = maxStepSeen > i || step > i;
        const interactive = i <= maxStepSeen || i <= step;
        return (
          <li key={label} className="flex items-center gap-2">
            <button
              type="button"
              disabled={!interactive}
              onClick={() => interactive && goTo(i)}
              aria-current={isActive ? "step" : undefined}
              data-testid={`step-pill-${i}`}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed ${
                isActive
                  ? "bg-navy text-white shadow-soft"
                  : isComplete
                  ? "bg-aqua/15 text-aqua-deep hover:bg-aqua/25"
                  : "bg-mist text-navy/55"
              }`}
            >
              <span
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${
                  isActive
                    ? "bg-aqua text-navy-deep"
                    : isComplete
                    ? "bg-aqua-deep text-white"
                    : "bg-white/80 text-navy/55"
                }`}
              >
                {isComplete && !isActive ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : (
                  i + 1
                )}
              </span>
              {label}
            </button>
            {i < STEP_LABELS.length - 1 && (
              <span className="hidden text-navy/30 sm:inline">/</span>
            )}
          </li>
        );
      })}
    </ol>
  );

  // ====== Render ======

  if (status === "success" && submission) {
    return (
      <div data-testid="service-request-page">
        <RequestConfirmation submission={submission} />
      </div>
    );
  }

  return (
    <div data-testid="service-request-page">
      {/* Compact hero */}
      <section
        data-testid="service-request-hero"
        aria-labelledby="service-request-h1"
        className="relative overflow-hidden border-b border-mist-line bg-white"
      >
        <div
          className="absolute inset-0 bg-brand-gradient-soft opacity-90"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 opacity-80"
          style={{
            backgroundImage:
              "radial-gradient(circle at 10% 18%, rgba(34,211,238,0.16) 0%, transparent 48%), radial-gradient(circle at 92% 86%, rgba(245,158,11,0.10) 0%, transparent 55%)",
          }}
          aria-hidden="true"
        />
        <div className="container-page relative py-10 sm:py-14">
          <span className="pill">
            <HelpCircle className="h-3 w-3 text-aqua-deep" />
            Demo intake flow
          </span>
          <h1
            id="service-request-h1"
            className="mt-4 max-w-3xl font-display text-3xl font-bold leading-[1.06] tracking-tight text-navy sm:text-4xl lg:text-5xl"
          >
            Request HVAC Service
          </h1>
          <p className="mt-4 max-w-2xl text-base text-navy/70 sm:text-lg">
            Tell us what&rsquo;s happening, choose your urgency, and we&rsquo;ll
            show the contractor exactly what&rsquo;s going on before the
            callback.
          </p>
          <div className="mt-5">
            <DemoNotice
              variant="dark"
              label="Demo intake flow — Sun Chaser Cooling is a sample site by PrismForge Digital. No real emergency dispatch."
            />
          </div>
        </div>
      </section>

      {/* Form card */}
      <section
        ref={formCardRef}
        data-testid="service-request-form-section"
        aria-labelledby="service-request-h1"
        className="relative bg-mist py-10 sm:py-14"
      >
        <div className="container-page">
          <div className="mx-auto max-w-3xl">
            <div className="rounded-3xl border border-mist-line bg-white p-5 shadow-lift sm:p-8">
              {renderPills()}

              <div className="my-7 h-px w-full bg-mist-line" aria-hidden="true" />

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                >
                  {step === 0 && (
                    <StepIssue
                      data={formData}
                      errors={errors}
                      setField={setField}
                    />
                  )}
                  {step === 1 && (
                    <StepHome
                      data={formData}
                      errors={errors}
                      setField={setField}
                      fileRef={fileRef}
                      onFileChange={onFileChange}
                      removePhoto={removePhoto}
                    />
                  )}
                  {step === 2 && (
                    <StepContact
                      data={formData}
                      errors={errors}
                      setField={setField}
                    />
                  )}
                  {step === 3 && <StepReview data={formData} goTo={goTo} />}
                </motion.div>
              </AnimatePresence>

              {/* Error banner */}
              {status === "error" && (
                <div
                  data-testid="service-request-server-error"
                  role="alert"
                  className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                >
                  <p className="font-semibold">Something went wrong</p>
                  <p className="mt-1 text-red-700/85">
                    {serverError} — your details are still here. Try again or,
                    if the network is being unreliable, save it as an offline
                    demo so you can still see the confirmation.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={retry}
                      data-testid="service-request-retry"
                      className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white hover:bg-navy-soft"
                    >
                      <ArrowRight className="h-3 w-3" />
                      Try again
                    </button>
                    {attempts >= 2 && (
                      <button
                        type="button"
                        onClick={() => submit({ offline: true })}
                        data-testid="service-request-offline-submit"
                        className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 bg-white px-4 py-2 text-xs font-semibold text-navy hover:border-aqua/45"
                      >
                        Save as demo only (offline)
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Footer nav */}
              <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={back}
                  disabled={step === 0 || status === "submitting"}
                  data-testid="service-request-back"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-mist-line bg-white px-5 py-3 text-sm font-semibold text-navy disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={next}
                    disabled={status === "submitting"}
                    data-testid="service-request-next"
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-navy-soft"
                  >
                    Next
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => submit()}
                    disabled={status === "submitting"}
                    data-testid="service-request-submit"
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-6 py-3.5 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02] disabled:cursor-wait disabled:opacity-80"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Submit service request
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <p className="mt-6 text-center text-[12px] text-navy/55">
              No real emergency dispatch · This site is a sample built by{" "}
              <Link
                to="/demo"
                className="text-navy underline-offset-2 hover:text-aqua-deep hover:underline"
              >
                PrismForge Digital
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
